"use client";

import { Cart, CartItem } from "@/lib/shopify/types";
import PrimaryButton from "@/components/shared/PrimaryButton";
import { EXTENDED_BOOK_ENABLED, MAX_BOOK_QUANTITY, formatUsd, isDigitalLine, lineAttr, linePageCount, safeHttpUrl } from "@/lib/comix/bagConfig";
import {
  bookLengthLabel,
  EXTENDED_INNER_PAGES,
  extendedUpcharge,
  STANDARD_INNER_PAGES,
  type BookPageCount,
} from "@/lib/comix/book-length";
import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import { trackEvent } from "@/lib/amplitude";
import { prepareExtendedScript } from "@/components/comix/bag/useBookLengthPicker";

function RemoveConfirmModal({
  title,
  onConfirm,
  onCancel,
}: {
  title: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Transition show appear as={Fragment}>
      {/* z-[310]: stacks above the bag drawer's own Dialog (now z-[300], bumped from z-50 to
          outrank PayPal's own iframes, up to z-index:200 — this nested dialog has to clear
          that same ceiling too). */}
      <Dialog onClose={onCancel} className="relative z-[310]">
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        </Transition.Child>
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="transition-all ease-out duration-150"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition-all ease-in duration-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-[320px] rounded-2xl bg-white p-5 text-center shadow-xl">
              <Dialog.Title className="text-[17px] font-semibold text-[#333]">
                Remove this item?
              </Dialog.Title>
              <p className="mt-1 truncate text-[14px] text-[#828282]">{title}</p>
              <div className="mt-5 flex gap-3">
                <button
                  type="button"
                  onClick={onCancel}
                  className="flex-1 rounded-xl border border-[#d2d2d2] py-2.5 text-[15px] font-medium text-[#333] hover:border-[#888]"
                >
                  Cancel
                </button>
                <PrimaryButton type="button" onClick={onConfirm} className="flex-1 !py-2.5 !text-[15px]">
                  Remove
                </PrimaryButton>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

function StepperButton({
  type,
  isDisabled,
  blocked,
  onClick,
}: {
  type: "plus" | "minus";
  isDisabled?: boolean;
  /** Truly non-functional (vs. `isDisabled`, which is dimmed-but-still-clickable —
   *  the minus button at qty 1 stays clickable to trigger the remove-item confirm). */
  blocked?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={blocked}
      aria-label={type === "plus" ? "Increase item quantity" : "Reduce item quantity"}
      style={{ WebkitTapHighlightColor: "transparent", background: "transparent", width: "28px", height: "28px", minWidth: "28px", minHeight: "28px" }}
      className={`appearance-none touch-manipulation flex h-7 w-7 items-center justify-center rounded-[8px] border border-[#d2d2d2] transition-colors ${
        isDisabled || blocked ? "text-[#d2d2d2]" : "text-[#333] hover:border-[#888]"
      } ${blocked ? "cursor-not-allowed" : ""}`}
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 12 12"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      >
        {type === "plus" ? <path d="M6 1v10M1 6h10" /> : <path d="M1 6h10" />}
      </svg>
    </button>
  );
}

function UpgradeToPrintedButton({
  line,
  onCartUpdate,
  setBusy,
}: {
  line: CartItem;
  onCartUpdate?: (cart: Cart) => void;
  /** Parent's setSyncing — shows the price/total skeleton while the swap round-trips. */
  setBusy: (v: boolean) => void;
}) {
  const [pending, setPending] = useState(false);

  async function upgrade() {
    if (pending) return;
    trackEvent("Bag - Upgrade to Printed", { runId: lineAttr(line, "_runId"), title: lineAttr(line, "_title") });
    setPending(true);
    setBusy(true);
    // A plain fetch to a Route Handler, deliberately NOT a "use server" Server Action —
    // invoking a Server Action re-renders this page's Server Components a moment later,
    // remounting the bag drawer's async wrapper (ComixBag) and closing the drawer. The
    // Route Handler does the identical Shopify mutation with none of that, and returns
    // the fresh cart so the drawer updates its own state. (Mirrors handleQtyChange.)
    let cart: Cart | null = null;
    try {
      const res = await fetch("/api/cart/upgrade-to-printed", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lineId: line.id }),
      });
      if (res.ok) cart = (await res.json()).cart ?? null;
    } catch {
      // cart stays null — handled below
    }
    if (cart) onCartUpdate?.(cart);
    // The line's quantity doesn't change on an upgrade, so the parent's line.quantity
    // effect won't fire to clear the skeleton — clear it here regardless of outcome.
    setBusy(false);
    setPending(false);
  }

  return (
    <button
      type="button"
      onClick={upgrade}
      disabled={pending}
      className={`inline text-left underline ${pending ? "cursor-not-allowed opacity-50" : "hover:text-[#333]"}`}
    >
      Get Printed (+$15)
    </button>
  );
}

function ChangeLengthModal({
  current,
  standardPrice,
  upcharge,
  onPick,
  onCancel,
}: {
  current: BookPageCount;
  /** Unit price of this line's format at the standard length — the Extended price adds `upcharge` on top. */
  standardPrice: number;
  /** Extended − standard price for this line's format. */
  upcharge: number;
  onPick: (pageCount: BookPageCount) => void;
  onCancel: () => void;
}) {
  const options: { pageCount: BookPageCount; price: number }[] = [
    { pageCount: STANDARD_INNER_PAGES, price: standardPrice },
    { pageCount: EXTENDED_INNER_PAGES, price: standardPrice + upcharge },
  ];
  return (
    <Transition show appear as={Fragment}>
      {/* Nested Headless UI Dialog (like RemoveConfirmModal) so clicks in it don't count as
          "outside" the bag drawer's own Dialog and close the bag. */}
      <Dialog onClose={onCancel} className="relative z-[60]">
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        </Transition.Child>
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="transition-all ease-out duration-150"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition-all ease-in duration-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-[320px] rounded-2xl bg-white p-5 text-center shadow-xl">
              <Dialog.Title className="text-[17px] font-semibold text-[#333]">
                Book length
              </Dialog.Title>
              <div className="mt-4 flex flex-col gap-2">
                {options.map(({ pageCount, price }) => {
                  const isCurrent = pageCount === current;
                  return (
                    <button
                      key={pageCount}
                      id={`btn-bag-length-${pageCount + 2}`}
                      type="button"
                      onClick={() => (isCurrent ? onCancel() : onPick(pageCount))}
                      className={`flex items-center justify-between rounded-xl border-2 px-4 py-3 text-[15px] transition-colors ${
                        isCurrent ? "border-[#F52F81] text-[#F52F81]" : "border-[#e0e0e0] text-[#333] hover:border-[#c2c2c2]"
                      }`}
                    >
                      <span className="font-semibold">{bookLengthLabel(pageCount)}</span>
                      <span>{formatUsd(price)}</span>
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={onCancel}
                className="mt-4 text-[14px] font-medium text-[#828282] hover:text-[#333]"
              >
                Cancel
              </button>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

export default function BagLineItem({
  line,
  onSyncChange,
  onCartUpdate,
  lineDiscount = 0,
}: {
  line: CartItem;
  /** Savings on this line from the 2nd-book discount (computeLineDiscounts). */
  lineDiscount?: number;
  onSyncChange?: (v: boolean) => void;
  /** Called with the fresh cart once a quantity change/removal round-trips successfully,
   *  so the drawer can update its own state directly — see handleQtyChange below for why. */
  onCartUpdate?: (cart: Cart) => void;
}) {
  const [coverError, setCoverError] = useState(false);
  const [optimisticQty, setOptimisticQty] = useState(line.quantity);
  const [isSyncing, setIsSyncing] = useState(false);
  const [removed, setRemoved] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [changingLength, setChangingLength] = useState(false);

  function setSyncing(v: boolean) {
    setIsSyncing(v);
    onSyncChange?.(v);
  }

  useEffect(() => {
    setOptimisticQty(line.quantity);
    setSyncing(false);
  }, [line.quantity]);

  const digital = isDigitalLine(line);
  const runId = lineAttr(line, "_runId");
  const title =
    lineAttr(line, "_title") || line.merchandise.product?.title || "Your comic";
  const coverImage = safeHttpUrl(
    lineAttr(line, "_coverImage") || lineAttr(line, "_generated")?.split(",")[0],
  );
  const previewUrl = safeHttpUrl(lineAttr(line, "_previewUrl"));

  const lineSubtotal = parseFloat(
    line.cost.subtotalAmount?.amount || line.cost.totalAmount.amount || "0",
  );
  const lineTotal = lineSubtotal - lineDiscount;
  const discounted = lineTotal < lineSubtotal - 0.001;
  const pageCount = linePageCount(line);
  const unitPrice = parseFloat(line.cost.amountPerQuantity?.amount || "0") || lineSubtotal / (line.quantity || 1);
  const lineUpcharge = extendedUpcharge(digital ? 'digital' : 'printed');
  const standardUnitPrice = pageCount === EXTENDED_INNER_PAGES ? unitPrice - lineUpcharge : unitPrice;

  async function changeLength(next: BookPageCount) {
    setChangingLength(false);
    trackEvent("Book Length - Selected", { runId, source: "bag", pageCount: next });
    setSyncing(true);
    let cart: Cart | null = null;
    try {
      // Route Handler, not a Server Action — same drawer-remount reason as upgrade-to-printed.
      const res = await fetch("/api/cart/upgrade-page-count", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lineId: line.id, pageCount: next }),
      });
      if (res.ok) cart = (await res.json()).cart ?? null;
    } catch {
      // cart stays null
    }
    if (cart) onCartUpdate?.(cart);
    if (cart && next === EXTENDED_INNER_PAGES) prepareExtendedScript(runId, lineAttr(line, "_sessionId"));
    // Quantity doesn't change, so the line.quantity effect won't clear the skeleton.
    setSyncing(false);
  }

  async function handleQtyChange(delta: number) {
    const newQty = optimisticQty + delta;
    // Native window.confirm() blocks the main thread for as long as the user takes to
    // decide (measured 1.7s+ in the field) and can fire while the drawer's own open
    // transition is still animating right after Add to Bag — the click that should
    // remove the item gets lost in that. Use a custom popup instead: the first click
    // on minus-at-qty-1 arms it (opening RemoveConfirmModal), the modal's own Remove
    // button re-calls this with the same delta to actually resolve it.
    if (newQty < 1 && !confirmingRemove) {
      setConfirmingRemove(true);
      return;
    }
    if (newQty < 1) {
      trackEvent("Bag - Item Removed", { runId, title });
      setRemoved(true);
      onSyncChange?.(true);
    } else {
      trackEvent("Bag - Quantity Changed", { runId, title, delta, newQty });
      setOptimisticQty(newQty);
      setSyncing(true);
    }
    // A plain fetch to a Route Handler, deliberately NOT the "use server" Server Action
    // this used to call — invoking a Server Action at all (even as a bare function call,
    // not through a <form action>) makes Next.js automatically re-render this page's
    // Server Components a few seconds later to pick up whatever it revalidated, and since
    // the bag drawer's server-side wrapper (ComixBag) is an async component, that re-render
    // remounts the client BagDrawer — silently resetting its `isOpen` state and closing the
    // drawer out from under the user. (router.refresh() was tried too, for the same reason
    // it's wrong here: it deliberately causes exactly that remount.) The Route Handler does
    // the identical Shopify mutation with none of that special-cased revalidation behavior,
    // and returns the fresh cart so this component's state can be updated directly.
    let result: Cart | null = null;
    try {
      const res = await fetch("/api/cart/update-quantity", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lineId: line.id,
          variantId: line.merchandise.id,
          quantity: newQty,
          attributes: line.attributes,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        result = data.cart ?? null;
      }
    } catch {
      // result stays null — handled below same as a non-OK response
    }
    if (result) {
      onCartUpdate?.(result);
      // The removal path set onSyncChange(true) directly (bypassing setSyncing, since this
      // line is unmounting and won't be around to receive a reset via its own line.quantity
      // effect) — so it has to clear it directly here too, on success. Without this the
      // drawer's Total skeleton hangs forever after every successful removal: setSyncing's
      // reset only ever ran for the surviving lines' own quantity-change flow, never for one
      // that just disappeared.
      if (newQty < 1) onSyncChange?.(false);
    } else {
      // Failed — don't leave the UI hanging on a busy state that'll never resolve.
      setSyncing(false);
      if (newQty < 1) onSyncChange?.(false);
    }
  }

  return (
    <AnimatePresence>
    {!removed && <motion.li
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.92, y: -8, height: 0, paddingTop: 0, paddingBottom: 0, overflow: 'hidden' }}
      transition={{ duration: 0.28, ease: "easeInOut" }}
      className="flex gap-4 px-5 py-4"
    >
      {/* Cover thumbnail with VIEW overlay */}
      <div className="relative h-[150px] w-[100px] flex-shrink-0">
        {coverImage && !coverError ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={coverImage}
            alt={title}
            onError={() => setCoverError(true)}
            className="h-full w-full rounded-[1px] object-cover"
            style={{ boxShadow: "-2px 2px 2px 0px rgba(0,0,0,0.25), -7px 4px 4px 0px rgba(0,0,0,0.05)" }}
          />
        ) : (
          <div className="h-full w-full rounded-[1px] bg-gradient-to-br from-pink-50 to-red-50" style={{ boxShadow: "-2px 2px 2px 0px rgba(0,0,0,0.25), -7px 4px 4px 0px rgba(0,0,0,0.05)" }} />
        )}
        <div
          className="absolute inset-0 rounded-[1px] pointer-events-none"
          style={{ background: "linear-gradient(270deg, rgba(255, 255, 255, 0.00) 98.87%, rgba(255, 255, 255, 0.30) 100%), linear-gradient(270deg, rgba(255, 255, 255, 0.00) 86.39%, rgba(255, 255, 255, 0.30) 100%)" }}
        />
        {previewUrl && (
          <a
            id={`link-bag-view-${line.id}`}
            href={previewUrl}
            onClick={() => trackEvent("Bag - Preview Viewed", { runId: lineAttr(line, "_runId"), title })}
            className="absolute left-1/2 -translate-x-1/2 rounded-full flex items-center justify-center text-[11px] font-bold text-[#333] bg-white/75 hover:bg-white/95 transition-colors"
            style={{
              bottom: "6px",
              width: "51px",
              height: "20px",
              letterSpacing: "0.5px",
              backdropFilter: "blur(54px)",
            }}
          >
            VIEW
          </a>
        )}
      </div>

      {/* Title, format, stepper */}
      <div className="flex min-w-0 flex-1 flex-col">
        <p className="truncate text-[17px] font-semibold text-[#333]">
          {title}
        </p>
        {/* Subtitle: format, then the book length after a comma (14 or 30 pages). The length is
            tappable to switch when NEXT_PUBLIC_COMIX_EXTENDED_BOOK is on; the price follows the variant. */}
        <div className="mt-0.5 text-[16px] text-[#828282]">
          {digital ? "Digital" : <>6&rdquo;x9&rdquo; printed comic</>},{" "}
          {EXTENDED_BOOK_ENABLED ? (
            <button
              id={`btn-bag-length-${line.id}`}
              type="button"
              onClick={() => {
                trackEvent("Book Length - Change Opened", { runId, source: "bag", pageCount });
                setChangingLength(true);
              }}
              disabled={isSyncing}
              className="inline text-left hover:text-[#333] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {bookLengthLabel(pageCount)}
            </button>
          ) : (
            bookLengthLabel(pageCount)
          )}
          {digital && (
            <>
              {" "}·{" "}
              <UpgradeToPrintedButton
                line={line}
                onCartUpdate={onCartUpdate}
                setBusy={setSyncing}
              />
            </>
          )}
        </div>
        {changingLength && (
          <ChangeLengthModal
            current={pageCount}
            standardPrice={standardUnitPrice}
            upcharge={lineUpcharge}
            onPick={changeLength}
            // Cancel, close, tapping outside / Escape, or tapping the length already selected.
            // Picking a different length goes through changeLength, which tracks "Selected".
            onCancel={() => {
              trackEvent("Book Length - Change Dismissed", { runId, source: "bag", pageCount });
              setChangingLength(false);
            }}
          />
        )}

        {confirmingRemove && (
          <RemoveConfirmModal
            title={title}
            onConfirm={() => handleQtyChange(-1)}
            onCancel={() => setConfirmingRemove(false)}
          />
        )}
        <div className="mt-auto flex items-center" style={{ gap: "6px" }}>
          <StepperButton
            type="minus"
            isDisabled={optimisticQty <= 1}
            onClick={() => handleQtyChange(-1)}
          />
          <span className="text-center text-[15px] text-[#333]" style={{ width: "18px" }}>
            {optimisticQty}
          </span>
          {!digital && (
            <StepperButton
              type="plus"
              isDisabled={optimisticQty >= MAX_BOOK_QUANTITY}
              blocked={optimisticQty >= MAX_BOOK_QUANTITY}
              onClick={() => handleQtyChange(1)}
            />
          )}
        </div>
      </div>

      {/* Price */}
      {/* self-start: sized to the price itself, not stretched to the row's full height. */}
      <div className="flex flex-col items-end self-start text-[16px] font-semibold text-[#333]">
        {isSyncing ? (
          <div className="h-5 w-12 animate-pulse rounded bg-[#e0e0e0]" />
        ) : discounted ? (
          <span>
            <span className="line-through">{formatUsd(lineSubtotal)}</span>{" "}
            {formatUsd(lineTotal)}
          </span>
        ) : (
          <span>{formatUsd(lineTotal)}</span>
        )}
      </div>
    </motion.li>}
    </AnimatePresence>
  );
}
